import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import DOMPurify from "dompurify";
import remarkGfm from "remark-gfm";
import { useState } from "react";

// Add LinkRenderer component with proper TypeScript types
const LinkRenderer = ({ href, children, ...props }: React.ComponentProps<"a">) => {
  return (
    <a href={href} target="_blank" rel="noreferrer" {...props}>
      {children}
    </a>
  );
};

// Custom table components for proper scrolling
const TableWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="overflow-x-auto -mx-5">
    <div className="min-w-full px-5">{children}</div>
  </div>
);

const Table = ({ children }: { children: React.ReactNode }) => {
  return (
    <table className="border-collapse" style={{ tableLayout: "fixed" }}>
      {children}
    </table>
  );
};

const Th = ({
  children,
  ...props
}: React.ThHTMLAttributes<HTMLTableHeaderCellElement>) => {
  return (
    <th
      {...props}
      style={{
        ...props.style,
        width: "180px",
        minWidth: "180px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {children}
    </th>
  );
};

const Td = ({ children, ...props }: React.TdHTMLAttributes<HTMLTableDataCellElement>) => {
  return (
    <td
      {...props}
      style={{
        ...props.style,
        wordWrap: "break-word",
        verticalAlign: "top",
      }}
    >
      {children}
    </td>
  );
};

// Custom Image component with error handling to prevent retries
const ImageRenderer = ({ src, alt, ...props }: React.ComponentProps<"img">) => {
  const [hasError, setHasError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Don't render if image failed to load
  if (hasError) return null;

  return (
    <img
      {...props}
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={(e) => {
        e.preventDefault();
        setHasError(true);
        // Remove the src to prevent further retries
        e.currentTarget.src = "";
      }}
      onLoad={() => {
        setHasLoaded(true);
      }}
      style={{
        ...props.style,
        opacity: hasLoaded ? 1 : 0,
        transition: "opacity 0.2s ease-in-out",
      }}
    />
  );
};

// Create stable component references to prevent infinite re-renders
const TableComponent = (props: React.TableHTMLAttributes<HTMLTableElement>) => (
  <TableWrapper>
    <Table>{props.children}</Table>
  </TableWrapper>
);

// Custom h1 component - render as h2 to cap maximum heading level at h2
const H1Renderer = ({ children, ...props }: React.ComponentProps<"h1">) => {
  return <h2 {...props}>{children}</h2>;
};

// Stable components object - created once outside the component
const markdownComponents = {
  a: LinkRenderer,
  table: TableComponent,
  th: Th,
  td: Td,
  img: ImageRenderer,
  h1: H1Renderer,
};

const MarkdownFormatter = ({
  content,
  className = "",
  isRichText = false,
}: {
  content: string;
  className?: string;
  isRichText?: boolean;
}) => {
  return isRichText ? (
    <div
      className={`prose prose-sm w-full max-w-full prose-table:table-auto prose-strong:text-inherit prose-headings:text-inherit ${className}`}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
    />
  ) : (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      className={`prose prose-sm w-full max-w-full prose-table:table-auto prose-strong:text-inherit prose-headings:text-inherit ${className}`}
      components={markdownComponents}
      children={content.replace(/\n/g, "  \n")}
    ></ReactMarkdown>
  );
};

export default MarkdownFormatter;
